// "Start a jam": open a live room on a seed and walk into it.
//
// A jam is one row (POST /seeds/:id/jams); its room hydrates from the seed the
// first time anybody opens it, so the room page is the whole rest of the
// gesture. Signed out, the sign-in dialog comes first and the jam starts once
// you're in — you never have to find the button again.

import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import type { JamSummary } from "@shared/api-types.js";
import { json, webApi } from "@web-shared/lib/web-api";
import { roomPath } from "@/lib/ctas";
import { useAuthGate } from "../auth";

export interface StartJam {
  start: (seedId: string) => void;
  /** The seed a jam is being opened on, while the request is out. */
  startingId: string | null;
  error: string | null;
}

export function useStartJam(): StartJam {
  const gate = useAuthGate();
  const navigate = useNavigate();
  const [startingId, setStartingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const start = useCallback(
    (seedId: string) => {
      gate.requireSignIn("start_jam", "Sign in to start a jam", () => {
        setStartingId(seedId);
        setError(null);
        webApi<JamSummary>(`/seeds/${encodeURIComponent(seedId)}/jams`, json("POST", {}))
          .then((jam) => navigate(roomPath(jam.id)))
          .catch((err: unknown) => {
            setError(err instanceof Error ? err.message : "Couldn't start the jam.");
            setStartingId(null);
          });
      });
    },
    [gate, navigate],
  );

  return { start, startingId, error };
}
